import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import { UInput } from '../../UInput.component'
import { PasswordStrengthBar } from '@/core/components/PasswordStrength/password-strength-bar.component'
import { PasswordInfoPopover } from '@/core/components/PasswordStrength/password-info-popover.component'
import OpenPasswordIcon from '@/core/assets/OpenPassword.svg?react'
import ClosePasswordIcon from '@/core/assets/ClosePassword.svg?react'
import { getFieldTestId } from '../field-helpers'
import type { FieldRenderer } from '../types'

const renderPasswordField: FieldRenderer = ({ field, ctx, t }) => {
    const key = field.key
    return (
        <Box key={key} sx={{ flex: 1 }} data-testid={getFieldTestId(ctx, key, 'password-field')}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb="6px"
                sx={{ pl: '5px', pr: '8px' }}
                data-testid={getFieldTestId(ctx, key, 'password-label-row')}>
                <Typography sx={{ fontSize: 16, fontWeight: 400, color: 'rgba(21, 56, 98, 0.5)' }} data-testid={getFieldTestId(ctx, key, 'password-label')}>
                    {t(field.label)}
                </Typography>
                <PasswordInfoPopover data-testid={getFieldTestId(ctx, key, 'password-info-popover')} />
            </Stack>
            <UInput
                type={ctx.showPassword ? 'text' : 'password'}
                value={ctx.form[key] as string}
                onChange={ctx.set(key)}
                fullWidth
                disabled={ctx.formDisabled}
                slotProps={{
                    htmlInput: {
                        'data-testid': getFieldTestId(ctx, key, 'input')
                    },
                    input: {
                        endAdornment: (
                            <InputAdornment position="end" data-testid={getFieldTestId(ctx, key, 'password-input-adornment')}>
                                <IconButton
                                    onClick={() => ctx.setShowPassword?.(v => !v)}
                                    edge="end"
                                    size="small"
                                    data-testid={getFieldTestId(ctx, key, 'toggle-password-button')}>
                                    {ctx.showPassword ? (
                                        <OpenPasswordIcon width={22} height={14} data-testid={getFieldTestId(ctx, key, 'open-password-icon')} />
                                    ) : (
                                        <ClosePasswordIcon width={22} height={12} data-testid={getFieldTestId(ctx, key, 'close-password-icon')} />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        )
                    }
                }}
                data-testid={getFieldTestId(ctx, key, 'uinput')}
            />
            {ctx.useComplexPassword && (
                <PasswordStrengthBar
                    password={(ctx.form[key] as string) || ''}
                    showDescription={false}
                    data-testid={getFieldTestId(ctx, key, 'password-strength-bar')}
                />
            )}
        </Box>
    )
}

renderPasswordField.areCtxEqual = (a, b) =>
    a.showPassword === b.showPassword && a.useComplexPassword === b.useComplexPassword && a.setShowPassword === b.setShowPassword

export default renderPasswordField
